import "@/styles/globals.css"; 
import type { AppProps } from "next/app";
import AuthInitializer from "@/components/AuthInitializer";
import AuthStatusWidget from "@/components/AuthStatusWidget";
import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import posthog from 'posthog-js';
import Script from 'next/script';
import { 
  hasConsent, 
  subscribeToConsentChanges, 
  hasUserMadeChoice 
} from '@/utils/cookieConsentUtil';
import { 
  initializeGTMConsent, 
  updateConsentState, 
  applyStoredConsent 
} from '@/utils/gtmConsent';

// Type definition for cookie preferences
interface CookiePreferences {
  essential: boolean;
  analytics: boolean;
  advertising: boolean;
  [key: string]: boolean;
}

// Расширение типов для Window
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
    google_tag_manager: any;
  }
}

export default function App({ Component, pageProps }: AppProps) {
  const router = useRouter();
  const [analyticsEnabled, setAnalyticsEnabled] = useState<boolean>(false);
  const [posthogInitialized, setPosthogInitialized] = useState<boolean>(false);

  // Предопределяем dataLayer и gtag до инициализации GTM
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.dataLayer = window.dataLayer || [];
      window.gtag = function() {
        window.dataLayer.push(arguments);
      };
      
      // Инициализируем GTM Consent Mode
      initializeGTMConsent();
    }
  }, []);

  // Обработчик загрузки GTM
  const handleGTMLoad = () => {
    console.log('[GTM] Google Tag Manager loaded');
    
    // Применяем сохраненные настройки согласия
    applyStoredConsent();
  };

  // Отслеживаем изменения согласия
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Проверяем текущее состояние согласия на аналитику
    const currentAnalyticsEnabled = hasConsent('analytics');
    setAnalyticsEnabled(currentAnalyticsEnabled);
    
    // Инициализируем PostHog только при наличии согласия
    if (currentAnalyticsEnabled && hasUserMadeChoice()) {
      initializePosthog();
    }
    
    // Подписываемся на изменения настроек согласия
    const unsubscribe = subscribeToConsentChanges((newPreferences: CookiePreferences) => {
      console.log('[Consent] Preferences changed:', newPreferences);
      const newAnalyticsEnabled = newPreferences.analytics;
      setAnalyticsEnabled(newAnalyticsEnabled);
      
      // Обновляем согласие в GTM при изменении настроек
      updateConsentState(newPreferences.analytics, newPreferences.advertising);
      
      // Обновляем состояние PostHog
      if (newAnalyticsEnabled && !analyticsEnabled) {
        initializePosthog();
      } else if (!newAnalyticsEnabled && analyticsEnabled && posthogInitialized) {
        posthog.opt_out_capturing();
      }
    });
    
    return () => {
      unsubscribe();
    };
  }, [analyticsEnabled]);

  // Initialize PostHog
  const initializePosthog = () => {
    if (posthogInitialized) {
      posthog.opt_in_capturing();
    } else {
      posthog.init('phc_dfLSIQAGgA350UPP9HEKtSkiWIRc2E3HCIUVSs44zWo', {
        api_host: 'https://eu.i.posthog.com',
        person_profiles: 'identified_only',
        capture_pageview: false,
      });
      setPosthogInitialized(true);
    }
    
    posthog.capture('$pageview');
  };

  useEffect(() => {
    const handleRouteChange = () => {
      if (analyticsEnabled && posthogInitialized) {
        posthog.capture('$pageview');
      }
    };
    
    router.events.on('routeChangeComplete', handleRouteChange);
    
    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router.events, analyticsEnabled, posthogInitialized]);

  return (
    <>
      {/* Google Tag Manager */}
      <Script 
        id="google-tag-manager" 
        strategy="afterInteractive"
        onLoad={handleGTMLoad}
      >
        {`
          (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
          new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
          j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
          'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
          })(window,document,'script','dataLayer','GTM-KC5G5WLD');
        `}
      </Script>

      <noscript>
        <iframe
          src="https://www.googletagmanager.com/ns.html?id=GTM-KC5G5WLD"
          height="0"
          width="0"
          className="hidden invisible"
        ></iframe>
      </noscript>

      <AuthInitializer />
      <Component {...pageProps} />
      {process.env.NODE_ENV === 'development' && <AuthStatusWidget />}
    </>
  );
}